import apiClient from './client';

/**
 * Forecast API Client
 * Handles volume and price forecast CRUD operations
 */

const forecastApi = {
  /**
   * Get all forecast data for a part's scenario
   * @param {string} partNumber - Part number
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @param {string} granularity - "monthly", "yearly", or "all" (default: "monthly")
   * @returns {Promise<{forecasts: Array, scenario: Object, granularity: string}>}
   */
  getForecastData: async (partNumber, scenarioName = 'Active', granularity = 'monthly', options = {}) => {
    const response = await apiClient.get(`/api/v1/parts/${partNumber}/forecast`, {
      params: { 
        scenario_name: scenarioName,
        granularity: granularity,
        ...(options.programId ? { program_id: options.programId } : {})
      }
    });
    return response.data.data;
  },

  /**
   * Create a new forecast row
   * @param {string} partNumber - Part number
   * @param {Object} forecastData - { year, volume_plan, price_plan, volume_actual?, price_actual?, notes? }
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @returns {Promise<Object>}
   */
  createForecast: async (partNumber, forecastData, scenarioName = 'Active') => {
    const response = await apiClient.post(
      `/api/v1/parts/${partNumber}/forecast`,
      forecastData,
      { params: { scenario_name: scenarioName } }
    );
    return response.data.data;
  },

  /**
   * Update a forecast row
   * @param {string} partNumber - Part number
   * @param {number} year - Year to update
   * @param {Object} updates - { volume_plan?, volume_actual?, price_plan?, price_actual?, notes?, version }
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @returns {Promise<Object>}
   */
  updateForecast: async (partNumber, year, updates, scenarioName = 'Active') => {
    const response = await apiClient.put(
      `/api/v1/parts/${partNumber}/forecast/${year}`,
      updates,
      { params: { scenario_name: scenarioName } }
    );
    return response.data.data;
  },

  /**
   * Delete a forecast row
   * @param {string} partNumber - Part number
   * @param {number} year - Year to delete
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @returns {Promise<Object>}
   */
  deleteForecast: async (partNumber, year, scenarioName = 'Active') => {
    const response = await apiClient.delete(
      `/api/v1/parts/${partNumber}/forecast/${year}`,
      { params: { scenario_name: scenarioName } }
    );
    return response.data;
  },

  /**
   * Bulk update multiple months with same values
   * @param {string} partNumber - Part number
   * @param {Array} months - Array of {year, month} objects
   * @param {Object} updates - Updates to apply {volume_plan?, price_plan?, notes?}
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @returns {Promise<Object>}
   */
  bulkUpdateForecast: async (partNumber, months, updates, scenarioName = 'Active') => {
    const response = await apiClient.post(
      `/api/v1/parts/${partNumber}/forecast/bulk-update`,
      { months, updates },
      { params: { scenario_name: scenarioName } }
    );
    return response.data.data;
  },

  /**
   * Update S&OP planning data for a specific year
   * @param {string} partNumber - Part number
   * @param {number} year - Year to update
   * @param {Object} planningData - S&OP planning values
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @returns {Promise<Object>}
   */
  updateSopPlanning: async (partNumber, year, planningData, scenarioName = 'Active', options = {}) => {
    const response = await apiClient.put(
      `/api/v1/parts/${partNumber}/forecast/sop-planning/${year}`,
      planningData,
      { params: { scenario_name: scenarioName, ...(options.programId ? { program_id: options.programId } : {}) } }
    );
    return response.data.data;
  },

  /**
   * Import forecast data from Excel file
   * @param {string} partNumber - Part number
   * @param {File} file - Excel file to import
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @param {boolean} dryRun - Preview mode (default: false)
   * @returns {Promise<Object>}
   */
  importForecastExcel: async (partNumber, file, scenarioName = 'Active', dryRun = false) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post(
      `/api/v1/parts/${partNumber}/forecast/import`,
      formData,
      {
        params: { 
          scenario_name: scenarioName,
          dry_run: dryRun
        },
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    );
    return response.data;
  },

  /**
   * Download forecast template with instructions
   * @param {string} partNumber - Part number
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @returns {Promise<Blob>}
   */
  downloadTemplate: async (partNumber, scenarioName = 'Active') => {
    const response = await apiClient.get(
      `/api/v1/parts/${partNumber}/forecast/template`,
      {
        params: { scenario_name: scenarioName },
        responseType: 'blob'
      }
    );
    
    // Trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${partNumber}_template_${new Date().toISOString().split('T')[0]}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return response.data;
  },

  /**
   * Export forecast data to Excel file
   * @param {string} partNumber - Part number
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @returns {Promise<Blob>}
   */
  exportForecastExcel: async (partNumber, scenarioName = 'Active') => {
    const response = await apiClient.get(
      `/api/v1/parts/${partNumber}/forecast/export`,
      {
        params: { scenario_name: scenarioName },
        responseType: 'blob'
      }
    );
    
    // Trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${partNumber}_forecast_${scenarioName}_${new Date().toISOString().split('T')[0]}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return response.data;
  },

  /**
   * Get price waterfall breakdown
   * @param {string} partNumber - Part number
   * @param {string} scenarioName - Scenario name (default: "Active")
   * @returns {Promise<Object>}
   */
  getPriceWaterfall: async (partNumber, scenarioName = 'Active') => {
    const response = await apiClient.get(
      `/api/v1/parts/${partNumber}/price-waterfall`,
      { params: { scenario_name: scenarioName } }
    );
    return response.data.data;
  }
};

export default forecastApi;
